import { isCustomPaymentName } from './checkout-payment-types';
import * as moment from 'moment';
import {BotOrder, Modifier, ModifierOption, Order, OrdersWithPayment} from '../_models/order';
import {Store} from '../_models/store';
import {OrderAdditionalTax, resolveAdditionalTaxIds} from '../_models/additional-tax';
import {computeAutoGratuities} from '../_models/auto-gratuity';

export class OrderHelpers{
  public static parsePayment(payment){
    const doubleFields = ['sub_total', 'tax', 'tip', 'discount', 'store_credit', 'total', 'delivery_charge', 'service_charge', 'auto_gratuity', 'order_fee']
    doubleFields.forEach(f => payment[f] = parseFloat(payment[f]))
    return payment
  }

  /**
   * ENG-4644: Compute local payment totals matching backend order_logic().
   * Used during ordering so cashiers see instant totals without a server call.
   * Exact totals are calculated server-side on flushNow (payment/submit).
   *
   * Computation order mirrors backend order_logic() (views.py:1043-1716).
   */
  /** ENG-5889: the flat order fee this order carries (0 when off, nothing to charge, or a gift-card load). */
  public static orderFeeFor(store: any, botOrder: any, foodSubtotal: number): number {
    if (!store?.order_fee_enabled) { return 0; }
    const fee = +store.order_fee_amount || 0;
    if (fee <= 0 || foodSubtotal <= 0 || botOrder?.is_gift_card_order) { return 0; }
    return +fee.toFixed(2);
  }

  /** The fee prints and displays as its own row ("line" mode) rather than inside the subtotal. */
  public static orderFeeIsLine(payment: any): boolean {
    return +(payment?.order_fee ?? 0) > 0 && (payment?.order_fee_display || '').toLowerCase() === 'line';
  }

  /** The row label in line mode: the store's label with a capital first letter. */
  public static orderFeeRowLabel(payment: any): string {
    const label = (payment?.order_fee_label || '').trim() || 'order fee';
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  /** "Subtotal includes a $0.39 order fee." or '' — the same sentence the receipt prints (subtotal mode only). */
  public static orderFeeNote(payment: any): string {
    const fee = +(payment?.order_fee ?? 0);
    if (!(fee > 0) || OrderHelpers.orderFeeIsLine(payment)) { return ''; }
    const label = (payment?.order_fee_label || '').trim() || 'order fee';
    return `Subtotal includes a $${fee.toFixed(2)} ${label}.`;
  }

  public static computeLocalTotals(owp: any): void {
    if (!owp?.items || !owp?.payment) return;

    const store = owp.store || {};
    const botOrder = owp.bot_order || {};
    const payment = owp.payment;
    const isPos = !!botOrder.is_pos;
    const isGiftCardOrder = !!botOrder.is_gift_card_order;

    // Payment type: payment.type comes from get_payment_type() ("CASH", "CREDIT", etc.)
    const paymentTypeStr = (payment.type || '').toUpperCase();
    const isCashPayment = paymentTypeStr === 'CASH' || paymentTypeStr === 'GIFT' || paymentTypeStr === 'OTHER'
      || isCustomPaymentName(store, paymentTypeStr);

    // ── 1. sub_total + non_taxable_total + abs_sub_total ──
    // Backend: get_sub_total() at views.py:915-987
    const items = owp.items.filter((i: any) => !i.is_removed);
    let sub = 0;
    let nonTaxableTotal = 0;

    for (const item of items) {
      const price = (+item.sale_price || +item.price || 0) * (+item.quantity || 1);
      sub += price;
      if (item.menu_item?.is_taxable === false) {
        nonTaxableTotal += price;
      }
    }

    const miscItems = owp.misc_items || [];
    let absMiscTotal = 0;
    for (const mi of miscItems) {
      const miPrice = (+mi.price || 0) * (+mi.quantity || 1);
      sub += miPrice;
      if (miPrice < 0) absMiscTotal += Math.abs(miPrice);
      if (mi.is_taxable === false) nonTaxableTotal += miPrice;
    }

    const absSub = sub + absMiscTotal;
    // ENG-5889: the flat order fee is inside the subtotal the cashier sees and inside the tax
    // base, on every tender. `sub` stays the food subtotal for the percent discounts, the cash
    // discount, the tip base and the service-charge base, exactly as order_logic does it.
    const orderFee = OrderHelpers.orderFeeFor(store, botOrder, sub);
    payment.order_fee = orderFee;
    payment.order_fee_label = (store.order_fee_label || '').trim() || 'order fee';
    payment.order_fee_display = store.order_fee_display === 'line' ? 'line' : 'subtotal';
    // As a line the fee is its own row and the subtotal stays the food subtotal; otherwise it is folded in.
    payment.sub_total = +(sub + (payment.order_fee_display === 'line' ? 0 : orderFee)).toFixed(2);

    // ── 2. promotional_discount (server-only, preserve) ──
    // Backend: offer_core.apply_promotion() — too complex for local
    let promoDiscount = +payment.promotional_discount || 0;
    if (sub < promoDiscount) promoDiscount = sub;  // backend caps it

    // ── 3. discount (online-only, POS = 0) ──
    // Backend: views.py:1244-1251 — first_order_offer + discount_offer_percentage
    const discount = isPos ? 0 : (+payment.discount || 0);

    // ── 4. instant_cash_discount (COMPUTE locally) ──
    // Backend: views.py:1256-1269
    let instantCashDiscount = 0;
    if (!isGiftCardOrder && sub !== promoDiscount) {
      if (store.enable_instant_cash_discount && +store.instant_cash_discount_percent > 0) {
        instantCashDiscount = +(+store.instant_cash_discount_percent / 100 * sub).toFixed(2);
      }
    }

    // ── 5. instant_discount (RE-COMPUTE locally — changes with sub_total) ──
    // Backend: views.py:1261-1267
    let instantDiscount = 0;
    if (botOrder.instant_discount != null && botOrder.instant_discount !== '') {
      if (botOrder.instant_discount_type != null) {
        if (+botOrder.instant_discount_type === 1) {
          instantDiscount = +(+botOrder.instant_discount / 100 * sub).toFixed(2);
        } else if (+botOrder.instant_discount_type === 2) {
          instantDiscount = +botOrder.instant_discount;
        }
      }
    }
    // Backend: if uninflated_sub_total == instant_discount, zero out cash discount
    if (+sub.toFixed(2) === +instantDiscount.toFixed(2)) instantCashDiscount = 0;

    payment.instant_discount = +instantDiscount.toFixed(2);
    payment.instant_cash_discount = +instantCashDiscount.toFixed(2);
    payment.discount = +discount.toFixed(2);

    // ── 5b. comps (order_comp.service.comp_totals) ──
    // A comped line is worth its full value; a whole-bill comp covers what the
    // discounts above left, so the bill lands on zero.
    let comp = 0;
    let compNonTaxable = 0;
    for (const item of owp.items || []) {
      if (!item.is_comped || item.is_removed) continue;
      let line = (+item.sale_price || 0) * (+item.quantity || 0);
      for (const mod of item.modifiers || []) {
        const price = +mod.sale_price || 0;
        line += mod.fixed_price ? price * (+item.quantity || 0) : price * (+mod.quantity || 1) * (+item.quantity || 0);
      }
      comp += line;
      if (item.menu_item?.is_taxable === false) compNonTaxable += line;
    }
    for (const mi of owp.misc_items || []) {
      if (!mi.is_comped) continue;
      const line = (+mi.price || 0) * (+mi.quantity || 0);
      comp += line;
      if (mi.is_taxable === false) compNonTaxable += line;
    }
    comp = Math.min(comp, sub);
    if (botOrder.is_comped) {
      comp = Math.max(sub - discount - promoDiscount - instantDiscount, 0);
      compNonTaxable = nonTaxableTotal;
      instantCashDiscount = 0;
      payment.instant_cash_discount = 0;
    }
    comp = +comp.toFixed(2);
    payment.comp = comp;
    if (comp > 0) nonTaxableTotal = Math.max(nonTaxableTotal - compNonTaxable, 0);

    // ── 6. net_sale and cash_net_sale ──
    // Backend: views.py:1270-1271
    const netSale = Math.max(sub - discount - promoDiscount - instantDiscount - comp, 0) + orderFee;
    const cashNetSale = Math.max(sub - discount - promoDiscount - instantCashDiscount - instantDiscount - comp, 0) + orderFee;

    // ── 7. tax (per-rate computation with non_taxable_total subtraction) ──
    // Backend: views.py:1276-1289
    if (botOrder.is_tax_exempt) {
      payment.tax = 0;
      payment.cash_tax = 0;
    } else {
      const stateRate = (+store.state_tax_percent || 0) / 100;
      const localRate = (+store.local_tax_percent || 0) / 100;
      const stateTax = +(netSale * stateRate).toFixed(2) - +(nonTaxableTotal * stateRate).toFixed(2);
      const localTax = +(netSale * localRate).toFixed(2) - +(nonTaxableTotal * localRate).toFixed(2);
      const stateCashTax = +(cashNetSale * stateRate).toFixed(2) - +(nonTaxableTotal * stateRate).toFixed(2);
      const localCashTax = +(cashNetSale * localRate).toFixed(2) - +(nonTaxableTotal * localRate).toFixed(2);
      payment.tax = Math.max(0, stateTax + localTax);
      payment.cash_tax = Math.max(0, stateCashTax + localCashTax);
    }

    // ── 7b. additional taxes (ENG additional-taxes) ──
    // Mirrors additional_tax.engine: per-item taxes (mixed beverage, sugary drink) on the
    // item's own line, reduced by the same net_sale / sub_total factor as the sales-tax
    // base. Sales tax stays in payment.tax; these land in payment.additional_tax.
    OrderHelpers.computeAdditionalTaxes(owp, items, sub, netSale, cashNetSale, !!botOrder.is_tax_exempt);
    const additionalTax = +payment.additional_tax || 0;
    const cashAdditionalTax = +payment.cash_additional_tax || 0;

    // ── 8. tip (recompute from tip_percent if set) ──
    // Backend: views.py:1308-1314
    let tip: number;
    if (botOrder.tip_percent != null && +botOrder.tip_percent > 0) {
      // Percent tips include the order fee (David 2026-10-24), like order_logic.
      tip = +((+botOrder.tip_percent / 100) * (absSub + orderFee)).toFixed(2);
      botOrder.tip = tip;
    } else {
      tip = +(+botOrder.tip || +payment.tip || 0);
    }
    payment.tip = +tip.toFixed(2);

    // ── 8b. auto gratuity (ENG-5810) ──
    // Mirrors auto_gratuity.engine: "parties of 6+ pay 18%" lines from the store's rules,
    // with the cashier's overrides on this check carried over from the last server payment
    // (waived rule ids stay waived, MANUAL lines stay on). It is a tip: it joins the card and
    // cash totals right after the guest's tip — before delivery, service charge and the
    // credit surcharge, so the surcharge percent includes it like the backend — and never
    // enters a tax base or "Total Taxes & Fees". `payment.tip` stays the guest's own tip.
    const gratuity = computeAutoGratuities(store.auto_gratuity_rules, botOrder, items, miscItems, sub, netSale, payment);
    payment.auto_gratuity = gratuity.total;
    payment.auto_gratuities = gratuity.lines;
    payment.auto_gratuities_waived = gratuity.waived;
    const autoGratuity = gratuity.total;

    // ── 9. initial total ──
    // Backend: views.py:1316-1317
    let total = +(netSale + payment.tax + additionalTax + tip + autoGratuity).toFixed(2);
    let cashTotal = +(cashNetSale + payment.cash_tax + cashAdditionalTax + tip + autoGratuity).toFixed(2);

    // ── 10. delivery_charge (COMPUTE locally) ──
    // Backend: views.py:1319-1324
    let deliveryCharge = 0;
    const occasion = (botOrder.occasion || '').toString().toUpperCase();
    if (occasion === 'DELIVERY' || occasion === '3') {
      deliveryCharge = +(+botOrder.delivery_fee || 0);
      total += deliveryCharge;
      cashTotal += deliveryCharge;
    }
    payment.delivery_charge = +deliveryCharge.toFixed(2);

    // ── 11. service_charge (with service_fee_threshold check) ──
    // Backend: views.py:1326-1403
    let serviceCharge = 0;
    let serviceChargeTitle = 'Service Charge';

    if (!isGiftCardOrder && isPos) {
      // Check service_fee_threshold feature flag
      const sftEnabled = store.feature_flag
        ? (typeof store.getFeatureFlag === 'function'
            ? store.getFeatureFlag('service_fee_threshold')
            : store.feature_flag?.['service_fee_threshold'])
        : false;
      const thresholdLimit = +store.service_fee_threshold_limit || 0;

      let shouldApply = true;
      if (sftEnabled) {
        shouldApply = sub > thresholdLimit;
      }

      if (shouldApply && sub !== promoDiscount && !botOrder.is_comped) {
        if (+store.service_charge_pos && botOrder.pos_service_charge_dollar_state) {
          serviceCharge = +store.service_charge_pos;
          serviceChargeTitle = store.service_charge_label_pos || 'Service Charge';
        } else if (+store.default_pos_service_charge && botOrder.pos_service_charge_percent_state) {
          serviceCharge = +((+store.default_pos_service_charge / 100) * sub).toFixed(2);
          serviceChargeTitle = store.pos_service_charge_title || 'Service Charge';
        }
      }
      total += serviceCharge;
      cashTotal += serviceCharge;
    }
    payment.service_charge = +serviceCharge.toFixed(2);

    // ── 11b. sales tax on the service charge (order/fee_tax.py) ──
    // Where the store's state taxes it (store.fee_tax, from the backend). Cash and card both pay it.
    const feeTaxOn = (amount: number): [number, number] => {
      if (!(amount > 0) || botOrder.is_tax_exempt) { return [0, 0]; }
      // Half-up to the cent, the same rule as the server (fee_tax.tax_on).
      const cents = (rate: number) => Math.round(amount * rate + 1e-9) / 100;
      return [cents(+store.state_tax_percent || 0), cents(+store.local_tax_percent || 0)];
    };
    const [scStateTax, scLocalTax] = store.fee_tax?.tax_service_charge_effective ? feeTaxOn(serviceCharge) : [0, 0];
    const serviceChargeTax = +(scStateTax + scLocalTax).toFixed(2);
    total += serviceChargeTax;
    cashTotal += serviceChargeTax;
    payment.service_charge_title = serviceChargeTitle;

    // ── 12. POS surcharge (COMPUTE locally, applied on running TOTAL) ──
    // Backend: views.py:1467-1492 — surcharge % is on total, not sub_total
    // NOTE: Backend uses if/elif where BOTH branches apply surcharge identically.
    // The service_fee_threshold has NO gating effect on surcharge — only on service_charge.
    // The if branch fires when flag.state && sub > threshold, the elif fires otherwise.
    // Both do the same thing, so surcharge is always applied when toggles are on.
    let surcharge = 0;
    let onlyCreditSurchargeTitle = 'Credit surcharge';

    if (!isGiftCardOrder && isPos) {
      if (sub !== promoDiscount && !botOrder.is_comped) {
        if (botOrder.pos_sur_charge_percent_state && +store.pos_surcharge_percent) {
          onlyCreditSurchargeTitle = store.pos_surcharge_percent_label || 'Credit surcharge';
          surcharge = +(total * (+store.pos_surcharge_percent / 100)).toFixed(2);
          total += surcharge;
        } else if (botOrder.pos_sur_charge_dollar_state && +store.pos_surcharge_dollar) {
          onlyCreditSurchargeTitle = store.pos_surcharge_dollar_label || 'Credit surcharge';
          surcharge = +store.pos_surcharge_dollar;
          total += surcharge;
        }
      }
    }

    // ── 12b. sales tax on the credit-only surcharge (card total only) ──
    // None when the order is paid another way: the surcharge is zeroed just below.
    const surchargeTaxed = surcharge > 0 && !isCashPayment && paymentTypeStr !== 'SPLIT' && !isGiftCardOrder
      && !!store.fee_tax?.tax_credit_surcharge_effective;
    const [suStateTax, suLocalTax] = surchargeTaxed ? feeTaxOn(surcharge) : [0, 0];
    const surchargeTax = +(suStateTax + suLocalTax).toFixed(2);
    total += surchargeTax;
    // Fee tax is sales tax: it shows in Tax on the order, the receipt and "Taxes & fees".
    payment.tax = +((+payment.tax || 0) + serviceChargeTax + surchargeTax).toFixed(2);
    payment.cash_tax = +((+payment.cash_tax || 0) + serviceChargeTax).toFixed(2);
    payment.service_charge_tax = serviceChargeTax;
    payment.surcharge_tax = surchargeTax;

    // ── 13. Surcharge zeroing for cash/gift/other/split payments ──
    // Backend: views.py:1592-1595 — payment_type in [1,4,5,6]
    if (isCashPayment || paymentTypeStr === 'SPLIT') surcharge = 0;
    if (isGiftCardOrder) surcharge = 0;

    payment.surcharge = +surcharge.toFixed(2);
    payment.only_credit_surcharge_title = onlyCreditSurchargeTitle;
    // pos_surcharge is same as surcharge for POS (backend uses single `surcharge` var)
    payment.pos_surcharge = isPos ? +surcharge.toFixed(2) : (+payment.pos_surcharge || 0);

    // ── 14. pos_service_charge (preserve existing) ──
    const posServiceCharge = +payment.pos_service_charge || 0;

    // ── 15. store_credit (preserve existing) ──
    // Backend: views.py:1404-1533 — complex logic with preferred_payment, server-only
    const storeCredit = +payment.store_credit || 0;
    if (storeCredit > 0) {
      total -= storeCredit;
      cashTotal -= storeCredit;
    }

    // Backend: views.py:1539-1540
    cashTotal += posServiceCharge;
    total += posServiceCharge;

    // ── 16. bot_order.total (Pay button state) — BEFORE refund/adj ──
    // Backend: views.py:1541-1548 — uses pre-refund total/cash_total
    // Must use payment_type_raw (backend field) for cash detection here
    if (botOrder && !botOrder.is_submitted) {
      const payRaw = (botOrder.payment_type_raw || '').toLowerCase();
      if (payRaw === 'cash' || payRaw === 'gift' || payRaw === 'other' || isCustomPaymentName(store, payRaw)) {
        botOrder.total = +Math.max(0, cashTotal).toFixed(2);
      } else {
        botOrder.total = +Math.max(0, total).toFixed(2);
      }
    }

    // ── 17. due_total (split payment, refund, adjustment) ──
    // Backend: views.py:1549-1569
    // IMPORTANT: Backend modifies `total` (not `total_due`) for refund/adj.
    // Response: payment.total = total (post-refund), payment.due_total = total_due (pre-refund)
    let totalDue = total;
    let cashTotalDue = cashTotal;

    if (!isGiftCardOrder) {
      const paidSoFar = +botOrder.paid_so_far || 0;
      if (paidSoFar > 0 && !botOrder.is_closed) {
        totalDue = total - paidSoFar;
        cashTotalDue = cashTotal - paidSoFar;
      } else {
        // totalDue stays as total (backend: total_due = total, not modified by refund)
        cashTotalDue = cashTotal;
        if (botOrder.has_refund && botOrder.refund_total != null) {
          const refund = +botOrder.refund_total;
          cashTotalDue -= refund;
          total -= refund;  // backend modifies total, NOT total_due
        }
        if (botOrder.has_adjustment) {
          const adj = +(+botOrder.adjusted_total || 0);
          cashTotalDue += adj;
          total += adj;     // backend modifies total, NOT total_due
        }
      }
    }

    // Set payment fields AFTER refund/adj so payment.total includes them
    // Backend response: "total" = round(total, 2), "cash_due_total" = cash_total_due
    payment.total = +Math.max(0, total).toFixed(2);
    payment.cash_due_total = +Math.max(0, cashTotalDue).toFixed(2);
    payment.due_total = +Math.max(0, totalDue).toFixed(2);

    // ── 18. consolidate taxes & fees ──
    // Backend: views.py:1742-1743, 1859-1864
    // R8: a consolidating store folds the additional tax into "Total Taxes & Fees" too.
    const consolidateCash = posServiceCharge + serviceCharge + payment.cash_tax + cashAdditionalTax;
    const consolidateCredit = posServiceCharge + serviceCharge + surcharge + payment.tax + additionalTax;
    payment.consolidate_taxes_and_fees_cash = +consolidateCash.toFixed(2);
    payment.consolidate_taxes_and_fees_credit = +consolidateCredit.toFixed(2);
    // Backend: bo_data['payment_type'] == "CASH" — only "CASH", not GIFT/OTHER
    if (!isGiftCardOrder) {
      payment.consolidate_taxes_and_fees = paymentTypeStr === 'CASH'
        ? +consolidateCash.toFixed(2) : +consolidateCredit.toFixed(2);
    }
  }

  /**
   * Additional taxes for one order, written onto `owp.payment` as
   * `additional_tax`, `cash_additional_tax` and `additional_taxes[]` — the same shape the
   * backend returns, so every breakdown template reads either without caring which
   * computed it.
   *
   * Rules (docs/additional-taxes/product-spec.md §3):
   *  - R1  base = line price × qty (+ modifiers when the tax says so), × net_sale / sub_total
   *  - R3  an item qualifies when assigned directly, or via its category and not excluded;
   *        the server-resolved `menu_item.additional_tax_ids` wins when present
   *  - R4  tax-exempt orders pay nothing; `is_taxable` does not matter here
   *  - R5  the cash figure uses cash_net_sale / sub_total
   *  - R6  sum each tax's base across lines, round the tax once, half-up to cents
   *  Misc items have no category and are never taxed here (non-goal for v1).
   */
  public static computeAdditionalTaxes(owp: any, items: any[], subTotal: number,
                                       netSale: number, cashNetSale: number, isTaxExempt: boolean): void {
    const payment = owp.payment;
    const taxes: any[] = (owp.store && owp.store.additional_taxes) || [];
    if (!taxes.length) {
      payment.additional_tax = 0;
      payment.cash_additional_tax = 0;
      payment.additional_taxes = [];
      return;
    }

    const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
    // Clamped like the sales-tax figures above: a fixed discount larger than the
    // subtotal must not turn the additional tax negative (backend: max(net, 0)).
    const clamp01 = (n: number) => Math.min(1, Math.max(0, n));
    const factor = subTotal > 0 ? clamp01(netSale / subTotal) : 0;
    const cashFactor = subTotal > 0 ? clamp01(cashNetSale / subTotal) : 0;

    // Base per tax, summed across lines before rounding (R6).
    const baseByTax = new Map<number, { withMods: number; noMods: number }>();
    for (const tax of taxes) { baseByTax.set(+tax.id, { withMods: 0, noMods: 0 }); }

    if (!isTaxExempt) {
      for (const item of items) {
        const menuItem = item.menu_item || {};
        const qty = +item.quantity || 1;
        // The order line's price already carries modifier upcharges (it is what sub_total
        // sums); the item's base price is the line without them.
        const lineWithMods = (+item.sale_price || +item.price || 0) * qty;
        const basePrice = +menuItem.base_price;
        const lineNoMods = isFinite(basePrice) && basePrice > 0 ? Math.min(lineWithMods, basePrice * qty) : lineWithMods;

        let taxIds: number[];
        if (Array.isArray(menuItem.additional_tax_ids)) {
          taxIds = menuItem.additional_tax_ids.map((id: any) => +id);
        } else {
          const menuGroupId = menuItem.menu_group ?? menuItem.menu_group_id ?? item.menu_group_id ?? null;
          taxIds = resolveAdditionalTaxIds(taxes, menuItem.menu_item_group ?? null, menuGroupId);
        }
        for (const id of taxIds) {
          const acc = baseByTax.get(+id);
          if (!acc) { continue; }
          acc.withMods += lineWithMods;
          acc.noMods += lineNoMods;
        }
      }
    }

    const lines: OrderAdditionalTax[] = [];
    let total = 0;
    let cashTotal = 0;
    for (const tax of taxes) {
      const acc = baseByTax.get(+tax.id);
      const rate = (+tax.rate_percent || 0) / 100;
      const rawBase = tax.applies_to_modifiers === false ? acc.noMods : acc.withMods;
      if (!rawBase) { continue; }   // backend omits taxes with no qualifying line
      const base = round2(rawBase * factor);
      const amount = round2(rawBase * factor * rate);
      const cashAmount = round2(rawBase * cashFactor * rate);
      total += amount;
      cashTotal += cashAmount;
      lines.push({
        id: tax.id,
        name: tax.name,
        label: tax.label && `${tax.label}`.trim()
          ? `${tax.label}`.trim()
          : `${tax.name} ${(+tax.rate_percent || 0).toFixed(4).replace(/\.?0+$/, '')}%`,
        rate_percent: tax.rate_percent,
        taxable_base: base,
        amount,
        cash_amount: cashAmount
      });
    }

    payment.additional_tax = round2(total);
    payment.cash_additional_tax = round2(cashTotal);
    payment.additional_taxes = lines;
  }

  public static parseOrderwithPayment(data){
    return Object.assign(new OrdersWithPayment(), data, {
      bot_order: Object.assign(new BotOrder(), data.bot_order, {
        occasion_schedule: moment(data.bot_order.occasion_schedule, 'hh:mm A MMM DD, YYYY').toDate()
      }),
      items: data.items.map(o => Object.assign(new Order(), o, {
        modifiers: o.modifiers.map(x  => Object.assign(new Modifier(), x, {
          modifier: Object.assign(new ModifierOption(), x.modifier)
        }))
      })),
      store: Object.assign(new Store(), data.store),
      payment: OrderHelpers.parsePayment(data.payment)
    });
  }
}

